import { listValues } from "./listRows";
import type { ListFieldFetch } from "./types";

export const MODELS_PREFIX = "models";

export const MODELS_HELPER_TEXT =
  "Only these models can be used with this connector. Leave empty to allow any model.";

export function connectorAllowedModels(
  configuration: Record<string, string>
): string[] {
  return listValues(configuration, MODELS_PREFIX);
}

export function openAiModelsExtract(
  body: string
): Array<Record<string, string>> {
  const parsed = JSON.parse(body) as {
    data?: Array<{ id?: unknown; created?: unknown }>;
  };
  return (parsed.data ?? [])
    .filter(
      (model): model is { id: string; created?: unknown } =>
        typeof model.id === "string"
    )
    .sort((a, b) => {
      const at = typeof a.created === "number" ? a.created : null;
      const bt = typeof b.created === "number" ? b.created : null;
      if (at === null && bt === null) return 0;
      if (at === null) return 1;
      if (bt === null) return -1;
      return bt - at;
    })
    .map((model) => ({ name: model.id }));
}

/** Gemini returns `{ models: [{ name }] }`, collection-qualified
 * (`models/gemini-2.5-pro`); store the bare id the API also accepts, so the
 * picker reads the way a model is written. */
export function geminiModelsExtract(
  body: string
): Array<Record<string, string>> {
  const parsed = JSON.parse(body) as { models?: Array<{ name?: unknown }> };
  return (parsed.models ?? [])
    .map((model) => model.name)
    .filter((name): name is string => typeof name === "string")
    .map((name) => ({ name: name.replace(/^models\//, "") }));
}

export function bedrockModelsExtract(
  body: string
): Array<Record<string, string>> {
  const parsed = JSON.parse(body) as {
    modelSummaries?: Array<{ modelId?: unknown }>;
  };
  return (parsed.modelSummaries ?? [])
    .map((model) => model.modelId)
    .filter((id): id is string => typeof id === "string")
    .map((id) => ({ name: id }));
}

/** `/models` on a connector-configured base URL, for gateways with no vendor default. */
export function configuredModelsEndpoint({
  baseUrl,
}: Record<string, string>): string {
  return `${(baseUrl ?? "").replace(/\/$/, "")}/models`;
}

export function openAiModelsFetch(
  label: string,
  request: (configuration: Record<string, string>) => string
): ListFieldFetch {
  return { label, request, extract: openAiModelsExtract };
}

export function withAllowedModelsHint(
  base: string,
  configuration: Record<string, string>
): string {
  const models = connectorAllowedModels(configuration);
  return models.length
    ? `${base} Only these models may be used with this connector: ${models.join(", ")}.`
    : base;
}
